/**
 * `hexSpace` — the hex realization of {@link Space}. A thin adapter: every
 * method delegates to the pure hex primitives (`coord`/`range`/`line`), which
 * stay the package's hex-specific core.
 *
 * Pixel layout (`hexToPixel`/`corners` in `layout.ts`) is deliberately **not**
 * here — it's a per-grid render concern, off the rules-facing `Space`.
 */
import { type Hex, add, distance, equals, hexKey, line, neighbors, parseHexKey, subtract } from "./coord";
import { footprint, range } from "./range";
import { type Space, cellRegion } from "./space";

export const hexSpace: Space<Hex> = {
  key: hexKey,
  parse: parseHexKey,
  equals,
  distance,
  neighbors,
  // Hexes share full edges — every step is edge-adjacent, so no step ever cuts a
  // corner and there is nothing to flank. (The no-corner-squeeze rule is square-only.)
  diagonalFlanks: () => [],
  line,
  disk: (center, r) => cellRegion(range(center, r), hexKey),
  footprint: (anchor, size) => cellRegion(footprint(anchor, size), hexKey),
  // Cube-coordinate vector add: p + (to − from). `Space.translate` is the one
  // sanctioned home for this algebra; the engine never does it itself.
  translate: (p, from, to) => add(p, subtract(to, from)),
};
